// ABOUTME: Modal for viewing photos in full size
// ABOUTME: Displays a single photo with navigation, and optional delete and download functionality

import { Box, useTheme } from "@mui/material";
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import SHModal from "../../theme/Modal/SHModal";
import Shbutton from "../../theme/buttons/Shbutton";
import { tokens } from "../../../theme";
import Text from "../../theme/text/Text";
import Subheading from "../../theme/text/Subheading";
import { downloadFile } from "../../../utils/downloadFile";

const MPhotoViewer = (props) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const {
        open,
        onClose,
        photos = [],
        currentPhotoIndex = 0,
        onPhotoChange,
        onDelete,
        isDeleting = false,
        showDownload = false,
    } = props;

    const currentPhoto = photos[currentPhotoIndex];
    const canGoBack = currentPhotoIndex > 0;
    const canGoForward = currentPhotoIndex < photos.length - 1;

    const handlePrevious = useCallback(() => {
        if (canGoBack && onPhotoChange) {
            onPhotoChange(currentPhotoIndex - 1);
        }
    }, [canGoBack, onPhotoChange, currentPhotoIndex]);

    const handleNext = useCallback(() => {
        if (canGoForward && onPhotoChange) {
            onPhotoChange(currentPhotoIndex + 1);
        }
    }, [canGoForward, onPhotoChange, currentPhotoIndex]);

    const handleDelete = () => {
        if (currentPhoto && onDelete) {
            onDelete(currentPhoto.id);
            // If this was the last photo or only photo, close modal
            if (photos.length === 1) {
                onClose();
            } else if (currentPhotoIndex === photos.length - 1) {
                // If deleting the last photo, move to previous
                onPhotoChange(currentPhotoIndex - 1);
            }
            // If deleting middle photo, index stays same but shows next photo
        }
    };

    const handleKeyDown = useCallback((e) => {
        if (e.key === "ArrowLeft") {
            handlePrevious();
        } else if (e.key === "ArrowRight") {
            handleNext();
        } else if (e.key === "Escape") {
            onClose();
        }
    }, [handlePrevious, handleNext, onClose]);

    useEffect(() => {
        if (open) {
            window.addEventListener("keydown", handleKeyDown);
            return () => window.removeEventListener("keydown", handleKeyDown);
        }
    }, [open, handleKeyDown]);

    if (!currentPhoto) return null;

    let isStandAlone = false;
    if (window.matchMedia('(display-mode: standalone)').matches) {
        isStandAlone = true;
    }

    return (
        <SHModal
            open={open}
            onClose={onClose}
        >
            {/* Header section with photo counter */}
            <Box
                sx={{
                    textAlign: "center",
                    paddingY: 4,
                    paddingX: 5,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    alignItems: "center"
                }}
            >
                <Subheading>{t("photos")}</Subheading>
                <Text bold>
                    {currentPhotoIndex + 1} / {photos.length}
                </Text>
                {currentPhoto.fileName && (
                    <Text noBreak>{currentPhoto.fileName}</Text>
                )}
            </Box>

            {/* Body - Image container */}
            <Box
                sx={{
                    background: theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                    padding: 3,
                    borderTopLeftRadius: 16,
                    borderTopRightRadius: 16,
                    height: "60vh",
                    maxHeight: 600,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    position: "relative",
                    overflow: "hidden",
                }}
            >
                <Box
                    component="img"
                    src={currentPhoto.url}
                    alt=""
                    sx={{
                        maxWidth: "100%",
                        maxHeight: "100%",
                        objectFit: "contain",
                    }}
                />
            </Box>

            {/* Footer with navigation and delete button */}
            <Box
                component="footer"
                sx={{
                    width: 1,
                    paddingBottom: isStandAlone ? "32px" : "16px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    paddingX: 3,
                    zIndex: 1058,
                    position: "relative",
                    paddingTop: 2,
                    background: `${theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0]}`,
                    borderBottomLeftRadius: 24,
                    borderBottomRightRadius: 24,
                    "@media screen and (max-width: 47.9375em)": {
                        paddingRight: 0,
                    }
                }}
            >
                {/* Left arrow */}
                <Shbutton
                    onClick={handlePrevious}
                    disabled={!canGoBack}
                    color="grey"
                    variant="contained"
                    className="icon"
                >
                    <ArrowBackIosNewIcon />
                </Shbutton>

                {/* Center - Download and delete buttons (each only shows when enabled) */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {showDownload && (
                        <Shbutton
                            onClick={() => downloadFile(currentPhoto.url, currentPhoto.fileName)}
                            color="primary"
                            variant="contained"
                            className="always-visible"
                            startIcon={<FileDownloadOutlinedIcon />}
                        >
                            {t("download")}
                        </Shbutton>
                    )}
                    {onDelete && (
                        <Shbutton
                            onClick={handleDelete}
                            disabled={isDeleting}
                            color="red"
                            variant="contained"
                            className="always-visible"
                            startIcon={<DeleteOutlineIcon />}
                        >
                            {t("delete")}
                        </Shbutton>
                    )}
                </Box>

                {/* Right arrow */}
                <Shbutton
                    onClick={handleNext}
                    disabled={!canGoForward}
                    color="grey"
                    variant="contained"
                    className="icon"
                >
                    <ArrowForwardIosIcon />
                </Shbutton>
            </Box>
        </SHModal>
    );
};

export default MPhotoViewer;
